// תור בשיחה (11 §1): הודעת המשתמש; תשובת הסוכן כרצף — טקסט מצטבר, tool_use/tool_result: בתור חי שורת עבודה אחת עם
// אנימציה (הכלי האחרון), אחרי done קיפול "N צעדים · עבד … · לפני …" עם רשימת הכלים (11, 2026-10-16), block כ-Screen עם "נכון ל-" שלו ו"הגדל" (ברוחב השיחה), סטטוס קונסנט, notice/error; הודעה שהמשתמש שלח באמצע התור (10 §1) — בועת
// משתמש במקום שנשלחה, עם "התקבל" כשהסוכן קיבל אותה; file — קובץ שהסוכן שלח (כרטיס הורדה + תצוגה); done — usage ל-admin בלבד.
// טקסט הסוכן — markdown מסונן (renderMarkdown של display: בלי HTML גולמי, קישורים פנימיים בלבד), dir=auto לכל בלוק.
import { memo, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { ExpandHostContext, plainDash, renderMarkdown, Screen, type ExpandHost } from '@aibify/display/client';
import { api } from '../../lib/api.ts';
import type { Outgoing, Part, TurnView as TurnT } from '../../lib/conversation.ts';
import { isLive } from '../../lib/conversation.ts';
import { T } from '../../lib/strings.ts';
import { useMinuteNow } from '../../lib/now.ts';
import { fmtSince, fmtWorked } from '../../lib/time.ts';
import { cn } from '../../lib/utils.ts';
import { StatusLabel } from '../ui/badge.tsx';
import { consentHref } from '../ConsentDrawer.tsx';
import { MailConnectCard } from '../MailConnectDrawer.tsx';
import { Button } from '../ui/button.tsx';
import { AttachmentList, FileCard } from './Attachments.tsx';

/**
 * טקסט הסוכן כ-markdown (רשימות, הדגשה, קוד, טבלאות). מגיע בזרם — מרנדרים את המצטבר (marked על 10KB ≈ 1ms לכל delta);
 * חלק טקסט שלא השתנה לא מרונדר מחדש (memo לפי הטקסט). לא נדחה (useDeferredValue): ההצמדה לסוף צריכה את הגובה האמיתי מיד.
 */
const AssistantText = memo(function AssistantText({ text }: { text: string }) {
  const html = useMemo(() => renderMarkdown(plainDash(text), { breaks: true, dirAuto: true }), [text]);
  // מסונן ב-renderMarkdown (marked + DOMPurify, רשימת תגיות סגורה)
  return <div className="ab-md ab-md-chat max-w-[75ch]" data-testid="assistant-text" dangerouslySetInnerHTML={{ __html: html }} />;
});

/** הודעה שיצאה מהמסך ועוד לא אושרה בשרת — מיד בתמליל; ממתינה / נשלחת / נכשלה (עם "שליחה שוב" ו"עריכה"). */
export function OutgoingBubble({ m, onRetry, onTakeBack }: { m: Outgoing; onRetry: () => void; onTakeBack: () => void }) {
  const label =
    m.state === 'failed'
      ? `${T.ai.out.failed}${m.errorHe ? `: ${m.errorHe}` : ''}`
      : m.state === 'waiting'
        ? T.ai.queuedSend
        : m.state === 'retrying'
          ? T.ai.out.retrying
          : m.progress != null && m.files.length
            ? T.ai.out.uploading(Math.round(m.progress * 100))
            : T.ai.out.sending;
  return (
    <li className="flex max-w-[85%] flex-col gap-0.5 self-start" data-testid="user-outgoing" data-state={m.state} data-local-id={m.localId}>
      <div className={cn('flex flex-col gap-2 rounded-md bg-primary-soft px-3 py-2', m.state === 'failed' ? 'border border-error/60' : 'opacity-80')}>
        {m.text && (
          <span dir="auto" className="whitespace-pre-wrap">
            {m.text}
          </span>
        )}
        {m.allFiles.length > 0 && (
          <ul className="m-0 flex list-none flex-wrap gap-1 p-0 text-xs text-muted-foreground">
            {m.allFiles.map((f, i) => (
              <li key={i} className="rounded bg-background/60 px-1.5">
                <bdi>{f.name}</bdi>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span role={m.state === 'failed' ? 'alert' : 'status'} className={cn('text-xs', m.state === 'failed' ? 'text-error' : 'text-muted-foreground')} data-testid="outgoing-state">
          {label}
        </span>
        {m.state === 'failed' && (
          <Button type="button" size="sm" variant="outline" onClick={onRetry} data-testid="outgoing-retry">
            {T.ai.out.resend}
          </Button>
        )}
        {(m.state === 'failed' || m.state === 'waiting') && (
          <Button type="button" size="sm" variant="ghost" onClick={onTakeBack} data-testid="outgoing-edit">
            {m.state === 'failed' ? T.ai.out.edit : T.ai.out.cancel}
          </Button>
        )}
      </div>
    </li>
  );
}

type ToolPart = Extract<Part, { kind: 'tool' }>;
/** מפתח יציב של בלוק בשיחה (הגדלה): התור + מקום החלק בו. */
export const blockKey = (turnId: string, partIndex: number) => `${turnId}:${partIndex}`;
const isTool = (p: Part): p is ToolPart => p.kind === 'tool';

/**
 * 11 (2026-10-16) שורת העבודה של התור החי: שורה אחת — ה-summaryHe של ה-tool_use האחרון ("חושב…" לפני הכלי הראשון,
 * "עוצר…" ב-stopping). tool_result לא נוגע בה; tool_use חדש מחליף את הטקסט (key חדש → מעבר עדין של 200ms). האנימציה
 * באלמנט נפרד שלא מתחלף בין כלים — לא נעצרת ולא מתחילה מחדש; גובה השורה קבוע (בלי קפיצה).
 */
function LiveLine({ turn }: { turn: TurnT }) {
  useHiddenFlag();
  let i = turn.parts.length - 1;
  while (i >= 0 && turn.parts[i]!.kind !== 'tool') i--;
  const tool = i >= 0 ? (turn.parts[i] as ToolPart) : null;
  const stopping = turn.status === 'stopping';
  const text = stopping ? T.ai.live.stopping : tool ? tool.summaryHe : T.ai.live.thinking;
  const key = stopping ? 'stop' : tool ? `t${i}` : 'think';
  return (
    <div role="status" aria-live="polite" className="ab-live" data-testid="turn-live" data-state={stopping ? 'stopping' : tool ? 'tool' : 'thinking'}>
      <span aria-hidden="true" className="ab-live-orb" data-testid="turn-live-anim">
        <span className="ab-live-glow" />
      </span>
      <span className="ab-live-slot">
        <span key={key} className="ab-live-text" data-testid="turn-live-text">
          {text}
        </span>
      </span>
    </div>
  );
}

/** טאב ברקע (document.hidden) → [data-hidden] על <html>; ה-CSS משהה את אנימציית השורה החיה (לא שורפים CPU ברקע). */
function useHiddenFlag() {
  useEffect(() => {
    const sync = () => document.documentElement.toggleAttribute('data-hidden', document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);
}

/**
 * אחרי done: שורת העבודה הופכת לקיפול אחד — "8 צעדים · עבד 1:47 דק' · לפני 3 דקות" (סגור כברירת מחדל); בפנים רשימת
 * הכלים כמו קודם, ול-admin ה-usage. הזמנים — מהשרת (sys.turn), "לפני" מחושב מול שעון הדפדפן ומתעדכן כל דקה.
 */
function StepsFold({ turn, isAdmin }: { turn: TurnT; isAdmin: boolean }) {
  const now = useMinuteNow();
  const tools = turn.parts.filter(isTool);
  const failed = tools.filter((p) => p.ok === false).length;
  const worked = fmtWorked(turn.startedAt, turn.finishedAt);
  const since = turn.finishedAt ? fmtSince(turn.finishedAt, now) : null;
  const usage = isAdmin && turn.usage ? T.ai.usage(turn.usage.inputTokens, turn.usage.cacheReadTokens ?? 0, turn.usage.outputTokens, turn.usage.costAgorot) : null;
  if (!tools.length && !worked && !since && !usage) return null;
  const line = (
    <>
      {tools.length > 0 && <span data-testid="turn-steps-count">{T.ai.steps(tools.length)}</span>}
      {failed > 0 && (
        <span className="text-error" data-testid="turn-steps-failed">
          {T.ai.stepsFailed(failed)}
        </span>
      )}
      {worked && <span data-testid="turn-worked">{worked}</span>}
      {since && (
        <time dateTime={turn.finishedAt!} data-testid="turn-since">
          {since}
        </time>
      )}
    </>
  );
  if (!tools.length && !usage)
    return (
      <p className="ab-steps-line m-0" data-testid="turn-steps" data-count={0}>
        {line}
      </p>
    );
  return (
    <details className="ab-steps" data-testid="turn-steps" data-count={tools.length}>
      <summary className="ab-steps-line" data-testid="turn-steps-summary">
        {line}
      </summary>
      <div className="flex flex-col gap-1 pt-2">
        {tools.map((p, i) => (
          <ToolLine key={i} p={p} />
        ))}
        {usage && (
          <span className="text-xs text-muted-foreground" data-testid="usage">
            {usage}
          </span>
        )}
      </div>
    </details>
  );
}

function ToolLine({ p }: { p: ToolPart }) {
  const state = p.ok === null ? (p.noResult ? 'noresult' : 'running') : p.ok ? 'ok' : 'fail';
  return (
    <details className="group rounded-md border border-border bg-muted/50 text-sm" data-testid="tool-line" data-tool={p.name} data-state={state}>
      <summary className="flex min-h-9 cursor-pointer list-none items-center gap-2 px-2">
        <span
          aria-hidden="true"
          className={cn('inline-block size-2 shrink-0 rounded-circle', state === 'running' ? 'animate-pulse bg-primary' : state === 'ok' ? 'bg-ok' : state === 'noresult' ? 'bg-muted-foreground' : 'bg-error')}
        />
        <span className="me-auto">{p.summaryHe}</span>
        <span className="text-muted-foreground">{state === 'running' ? T.ai.toolRunning : state === 'ok' ? T.ai.toolOk : state === 'noresult' ? T.ai.toolNoResult : T.ai.toolFail}</span>
      </summary>
      <div className="flex flex-col gap-1 border-t border-border px-2 py-1 text-muted-foreground">
        <bdi dir="ltr" className="text-xs">
          {p.name}
        </bdi>
        {p.resultHe && <span data-testid="tool-result">{p.resultHe}</span>}
      </div>
    </details>
  );
}

export function TurnBubble({
  turn,
  level,
  isAdmin,
  secrets,
  followedUp = false,
  isLast = false,
  onResend,
  expandHost,
}: {
  turn: TurnT;
  level: 1 | 2;
  isAdmin: boolean;
  secrets: Map<string, string>;
  /** נפתח תור המשך מהודעות באמצע התור הזה שלא נקלטו */
  followedUp?: boolean;
  isLast?: boolean;
  /** שליחה שוב של הודעה באמצע תור שלא הגיעה לסוכן (התור נעצר / הופסק) */
  onResend?: (text: string) => void;
  /** web-ai-stage-layout: ההגדלה של בלוק בתור (מפתח = turnId:partIndex) — שדה הכתיבה בתחתית, "תשובה חדשה · הצג", focus:'expand' מאייבי */
  expandHost?: (key: string) => ExpandHost;
}) {
  const loc = useLocation();
  const { conversationId } = useParams();
  const live = isLive(turn);
  const secretShown = turn.parts.flatMap((p) => (isTool(p) && p.secretKey && secrets.has(p.secretKey) ? [[p.secretKey, secrets.get(p.secretKey)!] as const] : []));
  return (
    <li className="flex flex-col gap-2" data-testid="turn" data-turn-id={turn.id} data-status={turn.status}>
      {(turn.userText || turn.attachments.length > 0) && (
        <div className="flex max-w-[85%] flex-col gap-2 self-start rounded-md bg-primary-soft px-3 py-2" data-testid="user-msg">
          {turn.userText && (
            <span dir="auto" className="whitespace-pre-wrap">
              {turn.userText}
            </span>
          )}
          <AttachmentList conversationId={conversationId} items={turn.attachments} />
        </div>
      )}
      <div className="flex max-w-full flex-col gap-3 self-stretch" data-testid="assistant-msg">
        <span className="text-xs font-medium text-muted-foreground">{T.ai.agent(level)}</span>
        {turn.parts.map((p, i) => {
          switch (p.kind) {
            case 'text':
              return <AssistantText key={i} text={p.text} />;
            case 'tool':
              // 11 (2026-10-16): אין רשימת כלים בתמליל — בתור חי שורת עבודה אחת (LiveLine), אחרי done קיפול אחד (StepsFold)
              return null;
            case 'block': {
              // הבלוק בתוך התשובה, ברוחב השיחה כולה (web-ai-stage-layout) — פעם אחת ב-DOM; "הגדל" מעביר את אותו אלמנט למסך מלא
              // compact (web-screen-budget): טבלה ארוכה — 12 שורות ו"עוד N שורות" שפותח הגדלה; בהגדלה — כל השורות
              const screen = <Screen screen={{ blocks: [p.block], asOf: p.asOf, notices: [] }} asOf={p.asOf} fetchFile={api.fetchFile} compact />;
              return (
                <div key={i} className="w-full min-w-0" data-testid="turn-block">
                  {expandHost ? <ExpandHostContext.Provider value={expandHost(blockKey(turn.id, i))}>{screen}</ExpandHostContext.Provider> : screen}
                </div>
              );
            }
            case 'file':
              return <FileCard key={p.file.id} conversationId={conversationId} file={p.file} captionHe={p.captionHe} />;
            case 'consent':
              return (
                <p key={i} className="m-0 flex flex-wrap items-center gap-2 text-sm" data-testid="turn-consent" data-status={p.status}>
                  <StatusLabel tone={p.status === 'pending' ? 'warn' : p.status === 'rejected' || p.status === 'expired' ? 'error' : 'ok'}>{T.ai.consentStatus[p.status] ?? p.status}</StatusLabel>
                  <Link to={consentHref(loc.search, p.consentId)} className="text-primary underline">
                    {T.ai.consentOpen}
                  </Link>
                </p>
              );
            case 'mail_connect':
              return <MailConnectCard key={i} caps={p.caps} />;
            case 'notice':
              return (
                <p key={i} role="status" className="m-0 rounded-md bg-muted p-2 text-sm">
                  {p.textHe}
                </p>
              );
            case 'error':
              return (
                <p key={i} role="alert" className="m-0 rounded-md border border-error/40 bg-error/5 p-2 text-sm">
                  {p.textHe}
                </p>
              );
            case 'user': {
              // לא נקלטה עד סוף התור: תור המשך נפתח ממנה ("תיענה בתור הבא"), או — עצירה/הפסקה, אין המשך — "לא הגיעה לסוכן" + שליחה שוב
              const lost = !p.heard && !live && !followedUp && isLast && (turn.status === 'canceled' || turn.status === 'error');
              const note = p.heard ? `${T.ai.midTurn} · ${T.ai.heard}` : followedUp && !live ? `${T.ai.midTurn} · ${T.ai.midFollowUp}` : lost ? `${T.ai.midTurn} · ${T.ai.midNotHeard}` : T.ai.midTurn;
              return (
                <div
                  key={i}
                  className="flex max-w-[85%] flex-col gap-0.5 self-start"
                  data-testid="user-mid-msg"
                  data-say-id={p.sayId}
                  data-heard={p.heard ? 'true' : 'false'}
                  data-lost={lost ? 'true' : undefined}
                >
                  <div className="flex flex-col gap-2 rounded-md bg-primary-soft px-3 py-2">
                    {p.text && (
                      <span dir="auto" className="whitespace-pre-wrap">
                        {p.text}
                      </span>
                    )}
                    <AttachmentList conversationId={conversationId} items={p.attachments} />
                  </div>
                  <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {note}
                    {lost && onResend && p.text && (
                      <Button type="button" size="sm" variant="outline" onClick={() => onResend(p.text)} data-testid="mid-resend">
                        {T.ai.out.resend}
                      </Button>
                    )}
                  </span>
                </div>
              );
            }
          }
        })}
        {live ? <LiveLine turn={turn} /> : <StepsFold turn={turn} isAdmin={isAdmin} />}
        {secretShown.map(([key, value]) => (
          // סוד (סיסמה זמנית) מ-tool_result: מוצג פעם אחת ליד ההודעה, מחוץ לקיפול; לא נשמר ב-state/storage
          <p key={key} className="m-0 rounded-sm border border-border px-2 py-1 text-sm" data-testid="tool-secret">
            <bdi dir="ltr" className="font-mono">
              {value}
            </bdi>{' '}
            <span className="text-xs text-muted-foreground">({T.ai.secretOnce})</span>
          </p>
        ))}
        {turn.status === 'canceled' && <span className="text-sm text-muted-foreground">{T.ai.canceled}</span>}
        {turn.status === 'timeout' && <span className="text-sm text-error">{T.ai.timeout}</span>}
        {turn.status === 'error' && <span className="text-sm text-error">{T.ai.errored}</span>}
      </div>
    </li>
  );
}
